import React from 'react';
import { Button, Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import StatusPill from '../StatusPill';
import PlanHeroNotice from './PlanHeroNotice';
import PlanHeroFacts from './PlanHeroFacts';
import { PAYMENT_PENDING_STATUSES } from './planHeroState';
import { MANAGE_BLOCKED_TEXT, UNDO_CANCEL_BLOCKED_TEXT } from './planHeroTexts';
import { primaryButtonSx, secondaryButtonSx } from '../buttonSx';
import { formatReais } from '../../utils/subscriptionFormat';
import styles from './PlanHeroCard.module.css';

const longDate = (value) => (value
  ? new Date(value).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })
  : null);

// Título e frase de apoio dos estados sem plano em andamento
const getEmptyCopy = (kind, subscription, trialDaysRemaining) => {
  switch (kind) {
    case 'internalTrial':
      return {
        name: 'Teste gratuito',
        text: trialDaysRemaining > 7
          ? 'Você está explorando a Gabriela gratuitamente.'
          : trialDaysRemaining >= 4
            ? 'Seu período de teste está chegando ao fim.'
            : 'Seu período de teste termina em breve.',
        cta: 'Assinar agora',
      };
    case 'trialExpired':
      return {
        name: 'Teste encerrado',
        text: 'Seu teste gratuito chegou ao fim. Escolha um plano para continuar usando o sistema.',
        cta: 'Ver planos',
      };
    case 'canceled':
      return {
        name: subscription?.plan_name || 'Assinatura encerrada',
        text: 'Para voltar a usar todos os recursos, escolha um plano.',
        cta: 'Ver planos',
      };
    default:
      return {
        name: 'Nenhum plano ativo',
        text: 'Escolha um plano para usar todos os recursos da sua conta.',
        cta: 'Ver planos',
      };
  }
};

const PlanHeroCard = ({
  subscription,
  heroState,
  nextCharge,
  trialInfo,
  trialDaysRemaining,
  pastDueInGrace,
  graceDeadline,
  canManage,
  onManage,
}) => {
  const { kind, status, pill } = heroState;
  const hasStripe = !!subscription?.stripe_subscription_id;
  const paymentPending = PAYMENT_PENDING_STATUSES.includes(status);
  const showsPlan = kind === 'active' || kind === 'paymentIssue';
  const copy = showsPlan ? null : getEmptyCopy(kind, subscription, trialDaysRemaining);

  const portalButton = hasStripe && kind === 'active' && (
    <Tooltip title={canManage ? '' : MANAGE_BLOCKED_TEXT} arrow>
      <span>
        <Button onClick={onManage} disabled={!canManage} sx={secondaryButtonSx}>
          {paymentPending ? 'Atualizar pagamento' : 'Cartão e faturas'}
        </Button>
      </span>
    </Tooltip>
  );

  return (
    <section className={styles.hero}>
      <div className={styles.top}>
        <span className={styles.eyebrow}>Seu plano</span>
        <StatusPill tone={pill.tone}>{pill.text}</StatusPill>
      </div>

      <div className={styles.identity}>
        <h2 className={styles.planName}>{showsPlan ? (subscription.plan_name || 'Plano') : copy.name}</h2>
        {showsPlan && subscription.price != null && (
          <p className={styles.price}>
            <span className={styles.priceValue}>{formatReais(subscription.price)}</span>
            <span className={styles.pricePeriod}>/mês</span>
          </p>
        )}
        {copy && <p className={styles.lead}>{copy.text}</p>}
      </div>

      <PlanHeroFacts
        kind={kind}
        status={status}
        subscription={subscription}
        nextCharge={nextCharge}
        trialEndsLabel={longDate(trialInfo?.trial_ends_at)}
        trialDaysRemaining={trialDaysRemaining}
      />

      {kind === 'active' && status === 'canceling' && (
        <PlanHeroNotice
          tone="warning"
          title="Cancelamento agendado"
          actionLabel="Desfazer cancelamento"
          onAction={onManage}
          canAct={canManage && hasStripe}
          blockedText={UNDO_CANCEL_BLOCKED_TEXT}
        >
          Até lá o plano segue ativo. Para desfazer, abra o portal de pagamento e renove a assinatura.
        </PlanHeroNotice>
      )}

      {kind === 'active' && status === 'past_due' && hasStripe && (
        <PlanHeroNotice
          tone={pastDueInGrace ? 'warning' : 'error'}
          title={pastDueInGrace ? 'Pagamento pendente' : 'Sua assinatura está suspensa'}
          actionLabel="Resolver pagamento"
          onAction={onManage}
          canAct={canManage}
        >
          {pastDueInGrace
            ? (graceDeadline
              ? `O acesso está mantido até ${graceDeadline}. Atualize a forma de pagamento até lá para continuar sem interrupção.`
              : 'O acesso está mantido por enquanto. Atualize a forma de pagamento para continuar sem interrupção.')
            : 'Houve um problema ao processar seu pagamento. Atualize a forma de pagamento para reativar o acesso.'}
        </PlanHeroNotice>
      )}

      {kind === 'paymentIssue' && (
        <PlanHeroNotice
          tone="error"
          title="Não foi possível processar seu pagamento"
          actionLabel="Resolver pagamento"
          onAction={onManage}
          canAct={canManage}
        >
          Atualize a forma de pagamento para reativar sua assinatura.
        </PlanHeroNotice>
      )}

      <div className={styles.actions}>
        <Button component={Link} to="/subscription/plans" sx={primaryButtonSx}>
          {copy ? copy.cta : 'Trocar plano'}
        </Button>
        {portalButton}
      </div>
      {portalButton && !canManage && <p className={styles.blockedNote}>{MANAGE_BLOCKED_TEXT}</p>}
    </section>
  );
};

export default PlanHeroCard;
